import { ComponentProps, memo } from 'react';
import { useFormContext } from 'react-hook-form';
import { Switch } from '@sib/ui';
import {
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from './BaseFormComponents';
import { FormComponentBaseProps } from './types/form-base-props';

export type FormSwitchProps = Omit<
  FormComponentBaseProps & ComponentProps<typeof Switch>,
  'onChange'
> & {
  onCheckedChange?: (checked: boolean) => void;
};

export const FormSwitch = ({
  description,
  descriptionProps,
  label,
  labelProps,
  hideError,
  defaultValue,
  onCheckedChange,
  name,
  disabled,
  ...props
}: FormSwitchProps) => {
  const { control } = useFormContext();

  return (
    <FormField
      shouldUnregister={false}
      disabled={disabled}
      name={name}
      defaultValue={defaultValue}
      control={control}
      render={({ field, fieldState }) => (
        <FormItem className="flex flex-row items-center rounded-md">
          <FormControl>
            <Switch
              {...field}
              {...props}
              checked={field.value}
              onCheckedChange={value => {
                onCheckedChange?.(value);
                field.onChange(value);
              }}
              id={`switch-${name}`}
            />
          </FormControl>
          <div className="self-center leading-4">
            <FormLabel
              className="cursor-pointer pl-2"
              htmlFor={`switch-${name}`}
              {...labelProps}
            >
              {label}
            </FormLabel>
            <FormDescription {...descriptionProps}>
              {description}
            </FormDescription>
            {!hideError && (
              <FormMessage>{fieldState.error?.message}</FormMessage>
            )}
          </div>
        </FormItem>
      )}
    />
  );
};

FormSwitch.displayName = 'FormSwitch';

export default memo(FormSwitch);
